import clsx from 'clsx';
import {
  deviceNames,
  type DefaultDeviceName,
  type DeviceName,
  type DeviceNameWithoutDefault,
} from './device';
import { objectKeys } from './object-utils';

// Note! These aren't breakpoints, but estimated maximum widths of the screen in each breakpoint.
export const screenSizes: Responsive<number> = {
  sm: 350,
  md: 800,
  lg: 1200,
};

// These are the default Tailwind breakpoints
export const breakpoints: Responsive<number> = {
  lg: 1024,
  md: 768,
  sm: 640,
};

export type Size =
  | 'none'
  | 'xsmall'
  | 'small'
  | 'gutter'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge';
export type ResponsiveSize = Partial<Responsive<Size>>;
export type Responsive<T> = Record<DeviceName, T>;
export type MaybeResponsive<T> = T | Responsive<T>;

export type ResponsiveClasses<TSize extends string | number = Size> = Record<
  DeviceNameWithoutDefault | 'default',
  Partial<Record<'default' | TSize, string>>
>;

export function getResponsiveClasses<TKey extends string | number = Size>(
  size: TKey | Partial<Record<DeviceName, TKey>> | undefined,
  responsiveClasses: ResponsiveClasses<TKey>,
) {
  if (!size) {
    return '';
  }

  if (typeof size === 'string' || typeof size === 'number') {
    return responsiveClasses['default'][size]!;
  } else {
    const classes: string[] = [];
    const defaultDeviceName: DefaultDeviceName = 'sm';
    for (const key of objectKeys(size)) {
      const deviceSize = size[key]!;
      if (key === defaultDeviceName && key in responsiveClasses) {
        // In some cases we want to differentiate between `default` and sm. Eg there's a difference between
        // using different colspans in grids if you want 4 colspans in all
        // screen sizes or if you want different colspans in different screen sizes.
        classes.push((responsiveClasses as Record<string, Record<TKey, string>>)[key][deviceSize]!);
      } else if (key === defaultDeviceName) {
        classes.push(responsiveClasses['default'][deviceSize]!);
      } else {
        classes.push(responsiveClasses[key][deviceSize]!);
      }
    }
    return clsx(classes);
  }
}

export function valueForPrevious<TValue>(
  deviceName: DeviceName,
  obj: Partial<Record<DeviceName, TValue>>,
): TValue | undefined {
  const deviceIndex = deviceNames.indexOf(deviceName);
  if (deviceIndex === 0) {
    return undefined;
  }
  const previousDeviceName = deviceNames[deviceIndex - 1];
  return obj[previousDeviceName] ?? valueForPrevious(previousDeviceName, obj);
}
